<template>
  <div class="roulette-admin-config">
    <!-- Header -->
    <div class="config-header">
      <h2 class="config-title">{{ $t('admin.config.roulette.title') }}</h2>
      <p class="config-subtitle">{{ $t('admin.config.roulette.subtitle') }}</p>
    </div>

    <div v-if="loading" class="loading-state">{{ $t('admin.config.loading') }}</div>
    <div v-else-if="loadError" class="error-banner">{{ loadError }}</div>

    <form v-else class="settings-form" @submit.prevent="saveConfig">

      <!-- Wheel Variant -->
      <section class="settings-section">
        <h3 class="section-title">{{ $t('admin.config.roulette.section_wheel_variant') }}</h3>

        <div class="form-row">
          <div class="form-label">
            {{ $t('admin.config.roulette.label_variant') }}
            <div class="radio-group">
              <label class="radio-label">
                <input v-model="form.variant" type="radio" value="european" class="radio-input" />
                <span>
                  European — 37 pockets (0–36)
                  <span class="rule-badge badge-info">House edge: 2.70%</span>
                </span>
              </label>
              <label class="radio-label">
                <input v-model="form.variant" type="radio" value="american" class="radio-input" />
                <span>
                  American — 38 pockets (0–36 + 00)
                  <span class="rule-badge badge-warning">House edge: 5.26%</span>
                </span>
              </label>
            </div>
            <span class="form-hint">American adds a double-zero pocket, nearly doubling the house edge.</span>
          </div>

          <div class="rtp-display">
            <span class="rtp-label">{{ $t('admin.config.label_est_rtp') }}</span>
            <span class="rtp-value">{{ rtp }}%</span>
            <span class="rtp-edge">{{ $t('admin.config.roulette.label_edge') }}: {{ houseEdge }}%</span>
          </div>
        </div>
      </section>

      <!-- Rule Options -->
      <section class="settings-section">
        <h3 class="section-title">{{ $t('admin.config.roulette.section_rule_options') }}</h3>

        <div class="form-row">
          <label class="toggle-label">
            <input v-model="form.la_partage" type="checkbox" class="toggle-checkbox" :disabled="form.variant === 'american'" />
            <span>
              {{ $t('admin.config.roulette.label_la_partage') }}
              <span class="rule-badge" :class="form.la_partage && form.variant === 'european' ? 'badge-info' : 'badge-disabled'">
                {{ form.la_partage && form.variant === 'european' ? 'Edge reduces to 1.35% on even-money bets' : 'European only' }}
              </span>
            </span>
          </label>
        </div>
        <p class="form-hint" style="margin-top: 4px;">
          On zero, even-money bets (Red/Black, Odd/Even, High/Low) lose only half their stake.
          European variant only.
        </p>

        <div class="form-row" style="margin-top: 14px;">
          <label class="toggle-label">
            <input v-model="form.neighbor_bets_enabled" type="checkbox" class="toggle-checkbox" />
            <span>Enable Neighbor Bets — Voisins du Zéro, Orphelins, Tiers du Cylindre</span>
          </label>
        </div>
      </section>

      <!-- UI Features -->
      <section class="settings-section">
        <h3 class="section-title">{{ $t('admin.config.roulette.section_ui_features') }}</h3>

        <div class="form-row">
          <label class="toggle-label">
            <input v-model="form.racetrack_enabled" type="checkbox" class="toggle-checkbox" :disabled="!form.neighbor_bets_enabled" />
            <span>
              {{ $t('admin.config.roulette.label_racetrack') }}
              <span v-if="!form.neighbor_bets_enabled" class="rule-badge badge-disabled">Requires neighbor bets</span>
            </span>
          </label>
        </div>
        <p class="form-hint" style="margin-top: 4px; margin-bottom: 14px;">
          Show oval racetrack UI for placing neighbor bets by clicking the wheel layout.
        </p>

        <div class="form-row">
          <label class="toggle-label">
            <input v-model="form.hot_cold_stats_enabled" type="checkbox" class="toggle-checkbox" />
            <span>Hot / Cold Statistics Panel — display number frequency and history strip</span>
          </label>
        </div>
      </section>

      <!-- History & Timing -->
      <section class="settings-section">
        <h3 class="section-title">{{ $t('admin.config.roulette.section_history_timing') }}</h3>

        <div class="form-row">
          <label class="form-label">
            {{ $t('admin.config.roulette.label_history_length') }}
            <input
              v-model.number="form.history_length"
              type="number"
              step="10"
              min="10"
              max="500"
              class="form-input"
              required
            />
            <span class="form-hint">Number of past results retained for hot/cold statistics. Default: 100.</span>
          </label>

          <label class="form-label">
            {{ $t('admin.config.roulette.label_spin_timer') }}
            <input
              v-model.number="form.spin_timer_seconds"
              type="number"
              step="1"
              min="0"
              max="60"
              class="form-input"
              required
            />
            <span class="form-hint">0 = disabled. Time between auto-advance rounds.</span>
          </label>
        </div>
      </section>

      <!-- Bet Limits -->
      <section class="settings-section">
        <h3 class="section-title">{{ $t('admin.config.section_bet_limits') }}</h3>

        <div class="form-row">
          <label class="form-label">
            {{ $t('admin.config.roulette.label_min_bet_zone') }}
            <input
              v-model.number="form.min_bet"
              type="number"
              step="0.01"
              min="0.01"
              class="form-input"
              required
            />
          </label>
          <label class="form-label">
            {{ $t('admin.config.roulette.label_max_bet_zone') }}
            <input
              v-model.number="form.max_bet"
              type="number"
              step="0.01"
              min="0.01"
              class="form-input"
              required
            />
          </label>
        </div>
        <div class="form-row">
          <label class="form-label">
            {{ $t('admin.config.roulette.label_table_limit') }}
            <input
              v-model.number="form.table_limit"
              type="number"
              step="0.01"
              min="0"
              class="form-input"
              required
            />
            <span class="form-hint">Caps total combined bets per spin. 0 = no cap.</span>
          </label>
        </div>
      </section>

      <div v-if="saveError" class="error-banner">{{ saveError }}</div>
      <div v-if="saveSuccess" class="success-banner">{{ saveSuccess }}</div>

      <div class="form-actions">
        <button type="submit" class="btn btn-primary" :disabled="saving">
          {{ saving ? $t('admin.config.saving') : $t('admin.config.save_config') }}
        </button>
        <span v-if="sourceLabel" class="source-badge" :class="sourceClass">
          {{ sourceLabel }}
        </span>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import apiClient from '@/api/client'

interface RouletteConfigResponse {
  source: 'database' | 'default'
  variant: 'european' | 'american'
  la_partage: boolean
  neighbor_bets_enabled: boolean
  racetrack_enabled: boolean
  hot_cold_stats_enabled: boolean
  history_length: number
  spin_timer_seconds: number
  min_bet: string
  max_bet: string
  table_limit: string
  rtp: number
  house_edge: number
}

const { t } = useI18n()

const loading = ref(true)
const saving = ref(false)
const loadError = ref('')
const saveError = ref('')
const saveSuccess = ref('')

const source = ref<'database' | 'default'>('default')
const rtp = ref(97.30)
const houseEdge = ref(2.70)

const form = reactive({
  variant: 'european' as 'european' | 'american',
  la_partage: false,
  neighbor_bets_enabled: true,
  racetrack_enabled: true,
  hot_cold_stats_enabled: true,
  history_length: 100,
  spin_timer_seconds: 0,
  min_bet: '1.00',
  max_bet: '1000.00',
  table_limit: '5000.00',
})

// Auto-clear La Partage when switching to American
watch(
  () => form.variant,
  (v) => {
    if (v === 'american') {
      form.la_partage = false
    }
  },
)

// Auto-disable racetrack when neighbor bets are disabled
watch(
  () => form.neighbor_bets_enabled,
  (v) => {
    if (!v) {
      form.racetrack_enabled = false
    }
  },
)

const sourceLabel = computed(() => (source.value === 'database' ? t('admin.config.saved_to_db') : t('admin.config.using_defaults')))
const sourceClass = computed(() => (source.value === 'database' ? 'source-db' : 'source-default'))

async function loadConfig() {
  loading.value = true
  loadError.value = ''
  try {
    const res = await apiClient.get<RouletteConfigResponse>('/admin/api/games/roulette/config')
    populateForm(res.data)
  } catch {
    loadError.value = t('admin.config.roulette.error_load')
  } finally {
    loading.value = false
  }
}

function populateForm(data: RouletteConfigResponse) {
  source.value = data.source
  rtp.value = data.rtp
  houseEdge.value = data.house_edge
  form.variant = data.variant
  form.la_partage = data.la_partage
  form.neighbor_bets_enabled = data.neighbor_bets_enabled
  form.racetrack_enabled = data.racetrack_enabled
  form.hot_cold_stats_enabled = data.hot_cold_stats_enabled
  form.history_length = data.history_length
  form.spin_timer_seconds = data.spin_timer_seconds
  form.min_bet = data.min_bet
  form.max_bet = data.max_bet
  form.table_limit = data.table_limit
}

async function saveConfig() {
  saveError.value = ''
  saveSuccess.value = ''
  saving.value = true

  try {
    const res = await apiClient.post<RouletteConfigResponse>('/admin/api/games/roulette/config', {
      variant: form.variant,
      la_partage: form.la_partage,
      neighbor_bets_enabled: form.neighbor_bets_enabled,
      racetrack_enabled: form.racetrack_enabled,
      hot_cold_stats_enabled: form.hot_cold_stats_enabled,
      history_length: form.history_length,
      spin_timer_seconds: form.spin_timer_seconds,
      min_bet: form.min_bet,
      max_bet: form.max_bet,
      table_limit: form.table_limit,
    })
    populateForm(res.data)
    saveSuccess.value = t('admin.config.roulette.save_success')
  } catch (err: unknown) {
    const e = err as { response?: { data?: { message?: string } } }
    saveError.value = e.response?.data?.message ?? t('admin.config.roulette.error_save')
  } finally {
    saving.value = false
  }
}

onMounted(loadConfig)
</script>

<style scoped>
.roulette-admin-config {
  padding: 0;
}

.config-header {
  margin-bottom: 24px;
}

.config-title {
  font-size: 1.375rem;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0 0 6px;
}

.config-subtitle {
  color: var(--text-secondary, #b1bad3);
  font-size: 0.875rem;
  margin: 0;
}

/* Loading / error / success */
.loading-state {
  color: var(--text-secondary, #b1bad3);
  padding: 32px;
  text-align: center;
}

.error-banner {
  background: rgba(239, 68, 68, 0.12);
  border: 1px solid rgba(239, 68, 68, 0.4);
  border-radius: 8px;
  color: var(--color-danger-light, #f87171);
  padding: 12px 16px;
  font-size: 0.875rem;
  margin-bottom: 16px;
}

.success-banner {
  background: rgba(26, 158, 110, 0.12);
  border: 1px solid rgba(26, 158, 110, 0.4);
  border-radius: 8px;
  color: var(--color-success-light, #34d399);
  padding: 12px 16px;
  font-size: 0.875rem;
  margin-bottom: 16px;
}

/* Form */
.settings-form {
  max-width: 720px;
}

.settings-section {
  background: var(--bg-secondary, #213743);
  border: 1px solid var(--border, #2d4a5a);
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 20px;
}

.section-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary, #fff);
  margin: 0 0 16px;
}

.form-row {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 14px;
  align-items: flex-start;
}

.form-row:last-child {
  margin-bottom: 0;
}

.form-label {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 0.8rem;
  color: var(--text-secondary, #b1bad3);
  flex: 1;
  min-width: 180px;
}

.form-input,
.form-select {
  padding: 8px 12px;
  background: var(--bg-primary, #0f212e);
  border: 1px solid var(--border, #2d4a5a);
  border-radius: 6px;
  color: var(--text-primary, #fff);
  font-size: 0.875rem;
}

.form-hint {
  font-size: 0.72rem;
  color: var(--text-secondary, #b1bad3);
  opacity: 0.7;
}

/* Radio group */
.radio-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 2px;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  color: var(--text-primary, #fff);
  cursor: pointer;
}

.radio-input {
  accent-color: var(--accent, #1a9e6e);
  width: 15px;
  height: 15px;
}

/* RTP display */
.rtp-display {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
  justify-content: center;
  min-width: 80px;
  padding-top: 18px;
}

.rtp-label {
  font-size: 0.75rem;
  color: var(--text-secondary, #b1bad3);
}

.rtp-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--color-success-light, #34d399);
}

.rtp-edge {
  font-size: 0.72rem;
  color: var(--text-secondary, #b1bad3);
}

/* Rule badge */
.rule-badge {
  display: inline-block;
  font-size: 0.68rem;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 4px;
  margin-left: 6px;
}

.badge-warning {
  background: rgba(251, 191, 36, 0.15);
  color: var(--color-warning-light, #fbbf24);
}

.badge-info {
  background: rgba(96, 165, 250, 0.15);
  color: var(--color-info-light, #60a5fa);
}

.badge-disabled {
  background: rgba(107, 114, 128, 0.15);
  color: var(--color-muted, #9ca3af);
}

/* Toggle */
.toggle-label {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.875rem;
  color: var(--text-primary, #fff);
  cursor: pointer;
  flex: 1;
  min-width: 200px;
}

.toggle-checkbox {
  width: 16px;
  height: 16px;
  accent-color: var(--accent, #1a9e6e);
  flex-shrink: 0;
}

.toggle-checkbox:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Actions row */
.form-actions {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 8px;
}

/* Source badge */
.source-badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 6px;
}

.source-db {
  background: rgba(59, 130, 246, 0.2);
  color: var(--color-info-light, #60a5fa);
}

.source-default {
  background: rgba(107, 114, 128, 0.2);
  color: var(--color-muted, #9ca3af);
}

/* Buttons */
.btn {
  padding: 9px 20px;
  border-radius: 7px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: opacity 0.15s;
}

.btn:disabled {
  opacity: 0.55;
  cursor: default;
}

.btn-primary {
  background: var(--accent, #1a9e6e);
  color: var(--color-on-dark, #fff);
}
</style>
